import {
  ShieldCheck,
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useCart } from "../../../context/CartContext";
import { userConfirmAction } from "../../../utils/toastUtils";
import { useState, useEffect } from "react";
import { convertTo12Hour } from "../../../utils/formatDateTime";

const PayAfterServiceCard = ({ bookingData, onSuccess, onBack }) => {
  const { submitBooking, submitting, totals, cart } = useCart();
  const { totalPrice } = totals;
  const [showServiceBreakdown, setShowServiceBreakdown] = useState(false);

  useEffect(() => {
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const handleConfirmBooking = async () => {
    const confirmed = await userConfirmAction({
      title: "Confirm Booking",
      text: "You can pay the service amount after the service is completed. Do you want to confirm this booking?",
      confirmText: "Confirm Booking",
      cancelText: "Cancel",
      icon: "info",
    });

    if (!confirmed) return;

    await submitBooking(
      {
        ...bookingData,
        paymentMethod: "PAY_AFTER_SERVICE",
        payment: {},
      },
      null,
    );

    onSuccess();
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-4">
      {/* BOOKING SUMMARY CARD */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header with Total */}
        <div className="bg-gradient-to-r from-primaryColor/5 to-primaryColor/10 px-4 py-4 border-b border-primaryColor/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900">₹{totalPrice}</p>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full">
                <ShieldCheck size={14} />
                <span className="text-xs font-semibold">Pay Later</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                After service completion
              </p>
            </div>
          </div>
        </div>

        {/* Services Breakdown */}
        <div className="px-4 py-3 border-b border-gray-100">
          <button
            onClick={() => setShowServiceBreakdown(!showServiceBreakdown)}
            className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900 transition"
          >
            <span>Services ({cart.length})</span>
            {showServiceBreakdown ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>

          {showServiceBreakdown && (
            <div className="mt-3 space-y-2">
              {cart.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1">
                  <span className="text-gray-600">
                    {item.title}{" "}
                    {item.qty > 1 && (
                      <span className="text-gray-400">× {item.qty}</span>
                    )}
                  </span>
                  <span className="font-medium text-gray-900">
                    ₹{item.discountedPrice * item.qty}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Appointment Details */}
        <div className="px-4 py-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Visit Details
          </h4>

          {/* Date & Time - Single Row */}
          {bookingData.slotSnapshot && (
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-3 pr-3">
                <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                  <Calendar size={16} className="text-primaryColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900">
                    {new Date(bookingData.slotSnapshot.date).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      },
                    )}
                  </p>
                </div>
              </div>

              <div className="h-8 w-px bg-gray-300"></div>

              <div className="flex items-center gap-3 flex-1">
                <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                  <Clock size={16} className="text-primaryColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900">
                    {convertTo12Hour(bookingData.slotSnapshot.startTime)} -
                    {convertTo12Hour(bookingData.slotSnapshot.endTime)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          {bookingData.location?.address?.formatted && (
            <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
              <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <MapPin size={16} className="text-primaryColor" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-900 leading-relaxed">
                  {bookingData.location.address.formatted}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PAYMENT PROCESS INFO */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <CheckCircle2 size={16} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              Payment Process
            </h4>
            <ol className="space-y-2 text-xs text-blue-800">
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[20px]">1.</span>
                <span>Confirm your booking by clicking the button below</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[20px]">2.</span>
                <span>Our professional will arrive at the scheduled time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[20px]">3.</span>
                <span>
                  Pay the total amount after service completion (Cash/UPI
                  accepted)
                </span>
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* CTA BUTTONS */}
      <div className="space-y-3">
        <button
          onClick={handleConfirmBooking}
          disabled={submitting}
          className="
            w-full
            bg-gradient-to-r
            from-[#f0653a] to-[#e4572f]
            hover:from-[#e4572f] hover:to-[#d94d28]
            text-white font-semibold
            py-4 rounded-xl
            shadow-md shadow-[#f0653a]/30
            hover:shadow-lg hover:shadow-[#f0653a]/40
            transition-all duration-200 ease-out
            active:scale-[0.97]
            focus:outline-none
            focus:ring-2 focus:ring-[#f0653a]/60 focus:ring-offset-2
            disabled:opacity-50
            disabled:cursor-not-allowed
            disabled:shadow-none
            flex items-center justify-center gap-2
          "
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Confirming...
            </>
          ) : (
            <>
              Confirm Booking
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </>
          )}
        </button>

        {onBack && (
          <button
            onClick={onBack}
            disabled={submitting}
            className="
              w-full
              border border-gray-300
              text-gray-700 font-medium text-sm
              py-3 rounded-xl
              hover:bg-gray-50
              transition
              disabled:opacity-50
            "
          >
            Back to Edit Slot
          </button>
        )}
      </div>

      {/* DISCLAIMER */}
      <p className="text-center text-xs text-gray-400 leading-relaxed">
        By confirming, you agree to pay ₹{totalPrice} to the service
        professional after completion
      </p>
    </div>
  );
};

export default PayAfterServiceCard;
